import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Avatar, Button, Tabs, Typography, Descriptions, Tag, Dropdown, Menu, Segmented, Form, Select, Space } from 'antd';
import {
    PlusCircleOutlined,
} from '@ant-design/icons';
import NotesComponent from '../../../components/Forms/NotesComponent';
import moment from 'moment';
import timeAgo from '../../../constants/functions';
import EditableFormBlock from '../../../components/Forms/EditableField';
import { MasterConfigs } from '../../../interfaces/entity';
import { constants } from '../../../constants/common';
import ProfileCard from '../../../components/ContactInfo/ProfileCard';
import Timeline from './../Timeline';
import Attachments from './../Attachments';
import { getData } from '../../../actions/apiCall';
import { useDispatch } from 'react-redux';
const { Title, Text } = Typography;

const DeveloperDetails: React.FC<{
    developer: any;
    addNew?: boolean;
    onClose?: () => void;
    formSubmitTrigger?: number;
}> = ({ developer, addNew, onClose, formSubmitTrigger }) => {
    const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const [highlighted, setHighlighted] = useState<string | null>(null);
    const [selectedDeveloper, setDeveloper] = useState<any>(null);
    const [editSkills, setEditSkills] = useState<boolean>(false);
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState<'overview' | 'timeline'>('overview');

    const developerOwnerConfig: MasterConfigs = {
        api: () => '/bench-developers',
        getFormColumns: () => [
            {
                label: 'Developer Owner',
                name: 'developerOwner',
                type: 'select',
                commonProps: {
                    disabled: true,
                },
                render: (text, data) => {
                    return constants.GET_FULL_NAME(text)
                },
                span: 24,
            },
            {
                label: 'First Name',
                name: 'firstName',
                type: 'text',
                inputProps: {
                    addonBefore: <Form.Item name="preName">
                        <Select style={{ width: 100 }} placeholder="Title">
                            {constants.PRENAME.map(ele => <Select.Option key={ele.value} value={ele.value}>{ele.name}</Select.Option>)}
                        </Select>
                    </Form.Item>
                },
                rules: [{ required: true, message: 'First Name is required' }],
                span: 24,
            },
            {
                label: 'Last Name',
                name: 'lastName',
                type: 'text',
                span: 24,
            },
            {
                label: 'Email',
                name: 'email',
                type: 'text',
                span: 24,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            return emailRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid email address'));
                        },
                    },
                ],
            },
            {
                label: 'Mobile',
                name: 'mobile',
                type: 'text',
                span: 24,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const mobileRegex = /^[0-9]{10,15}$/;
                            return mobileRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid mobile number'));
                        },
                    },
                ],
            },
            {
                label: 'Job Title',
                name: 'jobTitle',
                type: 'select',
                isCombo: true,
                span: 24,
                rules: [{ required: true, message: 'Job Title is required' }],
                render: (text) => {
                    return text?.name;
                }
            },
            {
                label: 'Experience',
                name: 'experience',
                type: 'text',
                inputProps: {
                    addonAfter: 'Years',
                },
                rules: [
                    { required: true, message: 'Experience is required' },
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const experienceRegex = /^[0-9]{1,3}$/;
                            return experienceRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid experience'));
                        },
                    },
                ],
                render: (text) => {
                    return <span>{text} Years</span>;
                },
                span: 24,
            },
            {
                label: 'LinkedIn Profile',
                name: 'linkedIn',
                type: 'text',
                span: 24,
                rules: [
                    {
                        required: true,
                        message: 'LinkedIn Profile is required',
                        validator: (_, value) => {
                            if (!value || (typeof value === 'string' && value.trim() === '')) {
                                return Promise.reject(new Error('LinkedIn Profile is required'));
                            }
                            const linkedInRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[\w-]+\/?$/i;
                            if (linkedInRegex.test(value.trim())) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                        },
                    },
                ],
                placeholder: 'https://www.linkedin.com/in/your-profile',
            },
        ],
        combo: []
    }

    const developerConfig: MasterConfigs = useMemo(() => ({
        api: () => '/bench-developers',
        getFormColumns: () => [
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Developer Information</Title>
            },
            {
                label: 'Developer Owner',
                name: 'developerOwner',
                type: 'select',
                isCombo: true,
                rules: [{ required: true, message: 'Developer Owner is required' }],
                span: 24,
                render: (text) => {
                    return [text?.firstName, text?.lastName].filter(Boolean).join(' ');
                }
            },
            {
                label: 'Developer',
                name: 'developer',
                type: 'select',
                isCombo: true,
                selectProps: {
                    showSearch: true,
                    allowClear: true,
                },
                span: 12,
                render: (text) => {
                    return [text?.firstName, text?.lastName].filter(Boolean).join(' ');
                }
            },
            {
                label: 'Vendor',
                name: 'vendorPartner',
                type: 'select',
                isCombo: true,
                span: 12,
                commonProps: {
                    disabled: selectedDeveloper,
                },
                render: (text) => {
                    return text?.vendorName || 'N/A';
                }
            },
            {
                label: 'First Name',
                name: 'firstName',
                type: 'text',
                inputProps: {
                    addonBefore: <Form.Item name="preName">
                        <Select style={{ width: 100 }} placeholder="Title">
                            {constants.PRENAME.map(ele => <Select.Option key={ele.value} value={ele.value}>{ele.name}</Select.Option>)}
                        </Select>
                    </Form.Item>
                },
                rules: [{ required: true, message: 'First Name is required' }],
                span: 12,
            },
            {
                label: 'Last Name',
                name: 'lastName',
                type: 'text',
                span: 12,
            },
            {
                label: 'Email',
                name: 'email',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            return emailRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid email address'));
                        },
                    },
                ],
            },
            {
                label: 'Mobile',
                name: 'mobile',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const mobileRegex = /^[0-9]{10,15}$/;
                            return mobileRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid mobile number'));
                        },
                    },
                ],
            },
            {
                label: 'LinkedIn Profile',
                name: 'linkedIn',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value || (typeof value === 'string' && value.trim() === '')) {
                                return Promise.resolve();
                            }
                            const linkedInRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[\w-]+\/?$/i;
                            if (linkedInRegex.test(value.trim())) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                        },
                    },
                ],
                placeholder: 'https://www.linkedin.com/in/your-profile',
            },
            {
                label: 'Job Title',
                name: 'jobTitle',
                type: 'select',
                isCombo: true,
                span: 12,
                rules: [{ required: true, message: 'Job Title is required' }],
                render: (text) => {
                    return text?.name;
                }
            },
            {
                label: 'Experience',
                name: 'experience',
                type: 'text',
                inputProps: {
                    addonAfter: 'Years',
                },
                render: (text) => {
                    return <span>{text || 'N/A'} Years</span>;
                },
                rules: [
                    { required: true, message: 'Experience is required' },
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const experienceRegex = /^[0-9]{1,3}$/;
                            return experienceRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid experience'));
                        },
                    },
                ],
            },
            {
                label: 'Developer Availability',
                name: 'developerAvailability',
                type: 'select',
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                children: constants.DEVELOPER_AVAILABILITY,
                rules: [{ required: true, message: 'Developer Availability is required' }],
                render: (text) => {
                    return constants.DEVELOPER_AVAILABILITY.find(ele => ele.value === text)?.name || 'N/A';
                },
                span: 12,
            },
            {
                label: 'Priority',
                name: 'priority',
                type: 'select',
                selectProps: {
                    options: [
                        { label: 'High', value: 'High' },
                        { label: 'Medium', value: 'Medium' },
                        { label: 'Low', value: 'Low' },
                    ],
                },
            },

            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Notice Information</Title>
            },
            {
                label: 'Notice Period',
                name: 'noticePeriodDays',
                type: 'text',
                inputProps: {
                    addonAfter: 'Days',
                },
                render: (text) => {
                    return <span>{text || 'N/A'} Days</span>;
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const noticePeriodRegex = /^[0-9]{1,3}$/;
                            return noticePeriodRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid notice period'));
                        },
                    },
                ]
            },
            {
                label: 'Last Working Date',
                name: 'lastWorkingDate',
                type: 'date',
                dateProps: {
                    format: 'DD/MM/YYYY',
                },
                render: (text) => {
                    return text ? moment(new Date(text)).format('DD/MM/YYYY') : 'N/A';
                },
            },
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Pricing Information</Title>
            },
            {
                label: 'Vendor Budget',
                name: 'vendorBudget',
                type: 'text',
                inputProps: {
                    prefix: 'INR ',
                },
                commonProps: {
                    disabled: selectedDeveloper,
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const rangeRegex = /^[0-9]+(\.[0-9]{1, 2})?$/;
                            return rangeRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid vendor billing rate'));
                        },
                    },
                ],
                render: (text) => {
                    return text ? `INR ${text}` : 'N/A';
                },
                span: 12,
            },
            {
                label: 'Selling Price',
                name: 'budgetRange',
                type: 'text',
                inputProps: {
                    prefix: 'INR ',
                },
                rules: [
                    { required: true, message: 'Selling Price is required' },
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.reject(new Error('Selling Price is required'));
                            const budgetRange = /^\s*\d+(\.\d{1, 2})?\s*-\s*\d+(\.\d{1, 2})?\s*$/;
                            return budgetRange.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid budget range, e.g. 10000 - 20000'));
                        },
                    },
                ],
                render: (text) => {
                    return text ? `INR ${text}` : 'N/A';
                },
                span: 12,
            },
            {
                label: 'Vendor GST / LUT',
                name: 'vendorGST',
                type: 'text',
                inputProps: {
                    defaultValue: 18,
                    addonAfter: '%',
                },
                commonProps: {
                    disabled: selectedDeveloper,
                },
                span: 12,
            },
            {
                label: 'TDS for Vendor',
                name: 'vendorTDS',
                type: 'text',
                inputProps: {
                    defaultValue: 10,
                    addonAfter: '%',
                },
                commonProps: {
                    disabled: selectedDeveloper,
                },
                span: 12,
            },
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Skills/Experience Information</Title>
            },
            {
                name: "skills",
                type: "form",
                span: 24,
                form: [
                    {
                        label: "Skill Name",
                        name: "skillName",
                        type: "DynamicSelect",
                        dynamicProps: {
                            keyName: 'skillName',
                        },
                        span: 8,
                        rules: [],
                    },
                    {
                        label: "Relevant Experience",
                        name: "relevantExperience",
                        type: "text",
                        rules: [{
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const experienceRegex = /^[0-9]{1,3}$/;
                                return experienceRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid experience'));
                            },
                        }],
                        render: (text) => text ? `${text} Years` : 'N/A',
                        span: 8,
                    },
                    {
                        label: "Ratings",
                        name: "rating",
                        type: "text",
                        rules: [{
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const ratingRegex = /^[0-9]{1,2}$/;
                                return ratingRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid rating'));
                            },
                        }],
                        render: (text) => text ? `${text} / 10` : 'N/A',
                        span: 8,
                    },
                ]
            },
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Address Information</Title>
            },
            {
                label: 'Street',
                name: ['address', 'street'],
                type: 'text',
                render: (text) => text || 'N/A',
                span: 12,
            },
            {
                label: 'City',
                name: ['address', 'city'],
                render: (text) => text || 'N/A',
                type: 'text',
                span: 12,
            },
            {
                label: 'State',
                name: ['address', 'state'],
                render: (text) => text || 'N/A',
                type: 'text',
                span: 12,
            },
            {
                label: 'Zip Code',
                name: ['address', 'zip'],
                render: (text) => text || 'N/A',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const zipRegex = /^[0-9]{4,10}$/;
                            return zipRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid zip code'));
                        },
                    },
                ],
            },
            {
                label: 'Country',
                type: 'text',
                name: ['address', 'country'],
                render: (text) => text || 'N/A',
                span: 12,
            },
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Description Information</Title>
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textarea',
                span: 24,
                textAreaProps: {
                    autoSize: { minRows: 3, maxRows: 6 }
                }
            },
        ],
        combo: [
            {
                api: '/users',
                name: 'developerOwner',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            },
            {
                api: '/users',
                name: 'developer',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            },
            {
                api: '/v1/vendor',
                name: 'vendorPartner',
                idKey: '_id',
                render: (text) => text?.vendorName || 'N/A'
            },
            {
                api: '/job-titles',
                name: 'jobTitle',
                idKey: '_id',
                render: (text) => text?.name || 'N/A'
            }
        ],
    }), [selectedDeveloper, editSkills]);

    useEffect(() => {
        if (formSubmitTrigger && formSubmitTrigger > 0 && addNew) {
            // Trigger form submission
            form.submit();
        }
    }, [formSubmitTrigger, addNew, form]);

    useEffect(() => {
        if (formSubmitTrigger && formSubmitTrigger > 0 && addNew) {
            // Trigger form submission when drawer Save button is clicked
            form.submit();
        }
    }, [formSubmitTrigger, addNew, form]);

    const getDeveloperDetails = async (data, completeData) => {
        const has = Object.keys(data).includes('developer');
        if (has && data.developer == null) {
            setDeveloper(null);
            form.setFieldsValue({
                firstName: '',
                lastName: '',
                email: '',
                mobile: '',
                address: {
                    street: '',
                    city: '',
                    state: '',
                    zip: '',
                    country: ''
                },
                description: '',
                developerOwner: completeData.developerOwner,
                developer: null,
                vendor: null,
                jobTitles: null
            });
            return;
        }

        if (!data.developer) {
            return;
        }
        setDeveloper(data.developer);
        try {
            const response = await dispatch(getData(`/users/${data.developer}`));
            if (response.code === 200) {
                form.setFieldsValue({ ...response.data, vendor: null });
            }
        } catch (error) {
            console.error('Error fetching developer details:', error);
            throw error;
        }
    }

    const sections = [
        { id: 'notes', title: 'Notes' },
        { id: 'attachments', title: 'Attachments' },
        { id: 'products', title: 'Products' },
        { id: 'open-activities', title: 'Open Activities' },
        { id: 'closed-activities', title: 'Closed Activities' },
        { id: 'invited-meetings', title: 'Invited Meetings' },
        { id: 'emails', title: 'Emails' },
        { id: 'campaigns', title: 'Campaigns' },
        { id: 'social', title: 'Social' },
    ];


    const scrollTo = (id: string) => {
        const section = sectionRefs.current[id];
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setHighlighted(id);

            setTimeout(() => {
                setHighlighted(current => (current === id ? null : current));
            }, 1000 * 5);
        }
    };

    return (
        <div className="flex overflow-hidden">
            {!addNew && <div className="w-64 bg-white shadow-sm border-r  h-[calc(100vh-60px)] p-4 flex-shrink-0">
                <Text strong className="block mb-4">Related List</Text>
                <ul className="text-sm">
                    {sections.map(section => (
                        <li
                            key={section.id}
                            onClick={() => scrollTo(section.id)}
                            className="cursor-pointer hover:bg-gray-100 py-2 px-2 rounded-xm group"
                        >
                            <div className='flex items-center justify-between h-5'>
                                {section.title}
                                <Button type='text' className='hidden group-hover:block' icon={<PlusCircleOutlined className='pt-1' />} size="small" />
                            </div>
                        </li>
                    ))}

                    <li className="text-blue-600 cursor-pointer hover:bg-gray-100 py-2 px-2 rounded-sm">Add Related List</li>
                </ul>
                <Text strong className="block mt-6 mb-2 ">Links</Text>
                <p className="text-blue-600 cursor-pointer text-sm hover:bg-gray-100 py-2 px-2 rounded-sm">Add Link</p>
            </div>}

            <div className="flex-1 overflow-y-auto h-full max-h-[calc(100vh-60px)] p-2">
                <div className="flex-1 p-2">
                    {!addNew && <div className='flex items-center justify-between mb-4 '>
                        <Segmented
                            options={[
                                { label: 'Overview', value: 'overview' },
                                { label: 'Timeline', value: 'timeline' },
                            ]}
                            value={activeTab}
                            onChange={value => setActiveTab(value as 'overview' | 'timeline')}
                        />
                        <div>
                            <Text className='text-sm text-gray-500'>Last Update : {timeAgo(developer?.updatedAt)}</Text>
                        </div>
                    </div>}

                    {activeTab === 'overview' && (<div className='space-y-4'>
                        {!addNew && <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <Form form={form} layout="vertical">
                                    <EditableFormBlock
                                        config={developerOwnerConfig}
                                        id={developer?._id}
                                        form={form}
                                    />
                                </Form>
                            </div>
                        </div>}
                        <div className="bg-white rounded-md mb-6">
                            <div className="flex justify-between items-center p-4">
                                <Form form={form} layout="vertical"
                                    onValuesChange={getDeveloperDetails}
                                    onFinish={async (values) => {
                                        try {
                                            const { postData, putData } = await import('../../../actions/developer');
                                            const get = (await import('lodash/get')).default;
                                            const set = (await import('lodash/set')).default;
                                            const payload: any = {};
                                            developerConfig.getFormColumns().forEach((column) => {
                                                const field = column.name;
                                                if (field) {
                                                    const val = get(values, field);
                                                    // Include field if it has a value (not undefined)
                                                    if (val !== undefined) {
                                                        const actualValue = val && typeof val === 'object' && '_id' in val ? val._id : val;
                                                        set(payload, field, actualValue);
                                                    }
                                                }
                                            });

                                            let resData;
                                            if (addNew || !developer?._id) {
                                                // Create new developer
                                                resData = await dispatch(postData(developerConfig.api(), payload));
                                            } else {
                                                // Update existing developer
                                                resData = await dispatch(putData(developerConfig.api() + '/' + developer._id, payload));
                                            }

                                            if (resData && onClose) {
                                                onClose();
                                            }
                                        } catch (error) {
                                            console.error('Failed to save:', error);
                                        }
                                    }}
                                >
                                    <EditableFormBlock
                                        addNew={addNew}
                                        config={developerConfig}
                                        id={addNew ? null : developer?._id}
                                        form={form}
                                        onClose={() => onClose && onClose()}
                                        hideButtons={true}
                                    />
                                </Form>
                            </div>
                        </div>

                        {!addNew && sections.map(section => (
                            <div
                                key={section.id}
                                ref={el => (sectionRefs.current[section.id] = el)}
                                className="bg-white rounded-md mb-6"
                            >
                                {!['Attachments'].includes(section.title) && <div className="flex justify-between items-center py-2 px-4 border-b">
                                    <Title level={5} className={highlighted === section.id ? 'bg-yellow-200 px-1' : ''}>{section.title}</Title>
                                    {section.title !== 'Notes' && <Button type="text" disabled>Add {section.title}</Button>}
                                </div>}
                                {!['Notes', 'Attachments'].includes(section.title) && <div className="p-4 text-gray-300 text-center">No {section.title.toLowerCase()} available</div>}
                                {section.title === 'Notes' && <NotesComponent refType={'BenchDevelopers'} refId={developer?._id} />}
                                {section.title === 'Attachments' && <div className='p-4'><Attachments refType={'BenchDevelopers'} refId={developer?._id} /> </div>}
                            </div>
                        ))}
                    </div>)}
                    {!addNew && activeTab === 'timeline' && <Timeline type="BenchDevelopers" id={developer?._id} />}
                </div>
            </div>
        </div>
    );
};

export default DeveloperDetails;